import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { eventApi, babyApi } from '../services/api';
import { getBabyKey, decryptEventData, decryptData, decryptBabyProfile } from '../crypto';
import { format, formatDistanceToNow, isToday, isYesterday, subDays } from 'date-fns';
import { EventType } from '../types';
import type { Event, DecryptedEvent, EventData, ProfileType } from '../types';

const EVENT_TYPE_INFO: Record<string, { icon: string; label: string; color: string }> = {
  FEED: { icon: '🍼', label: 'Feed', color: 'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800' },
  NAPPY: { icon: '🧷', label: 'Nappy', color: 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800' },
  SPIT_UP: { icon: '💧', label: 'Spit Up', color: 'bg-orange-100 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800' },
  MEDICATION: { icon: '💊', label: 'Medication', color: 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800' },
  SLEEP: { icon: '😴', label: 'Sleep', color: 'bg-purple-100 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800' },
  WEIGHT: { icon: '⚖️', label: 'Weight', color: 'bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800' },
  TEMPERATURE: { icon: '🌡️', label: 'Temperature', color: 'bg-pink-100 dark:bg-pink-900/30 border-pink-200 dark:border-pink-800' },
  TUMMY_TIME: { icon: '🏋️', label: 'Tummy Time', color: 'bg-teal-100 dark:bg-teal-900/30 border-teal-200 dark:border-teal-800' },
  PUMP: { icon: '🥛', label: 'Pump', color: 'bg-indigo-100 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800' },
  BOTTLE: { icon: '🍶', label: 'Bottle', color: 'bg-cyan-100 dark:bg-cyan-900/30 border-cyan-200 dark:border-cyan-800' },
  JOURNAL: { icon: '📝', label: 'Journal', color: 'bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700' },
  EC: { icon: '🚽', label: 'EC Time', color: 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800' },
  SYMPTOM: { icon: '🤢', label: 'Symptom', color: 'bg-pink-100 dark:bg-pink-900/30 border-pink-200 dark:border-pink-800' },
  APPOINTMENT: { icon: '🏥', label: 'Appointment', color: 'bg-indigo-100 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800' },
  BLOOD_PRESSURE: { icon: '🩺', label: 'Blood Pressure', color: 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800' },
  ULTRASOUND: { icon: '📷', label: 'Ultrasound', color: 'bg-purple-100 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800' },
  KICK_COUNT: { icon: '🦶', label: 'Kick Count', color: 'bg-rose-100 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800' },
  CONTRACTION: { icon: '⏱️', label: 'Contraction', color: 'bg-orange-100 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800' },
};

const BABY_EVENT_TYPES: EventType[] = [EventType.FEED, EventType.NAPPY, EventType.SPIT_UP, EventType.MEDICATION, EventType.SLEEP, EventType.WEIGHT, EventType.TEMPERATURE, EventType.TUMMY_TIME, EventType.PUMP, EventType.BOTTLE, EventType.JOURNAL, EventType.EC];
const PREGNANCY_EVENT_TYPES: EventType[] = [EventType.SYMPTOM, EventType.APPOINTMENT, EventType.BLOOD_PRESSURE, EventType.ULTRASOUND, EventType.KICK_COUNT, EventType.CONTRACTION, EventType.WEIGHT, EventType.MEDICATION, EventType.TEMPERATURE, EventType.JOURNAL];

function formatEventTime(date: Date): string {
  if (isToday(date)) {
    return `Today at ${format(date, 'h:mm a')}`;
  }
  if (isYesterday(date)) {
    return `Yesterday at ${format(date, 'h:mm a')}`;
  }
  return format(date, 'MMM d, yyyy h:mm a');
}

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function getEventSummary(type: EventType, data: EventData): string {
  switch (type) {
    case 'FEED':
      if (data.feedType === 'breast') {
        const dur = data.duration ? ` for ${formatMinutes(data.duration)}` : '';
        return `Breastfed${data.side ? ` (${data.side})` : ''}${dur}`;
      }
      return `${data.feedType || 'Fed'}${data.amount ? ` ${data.amount}ml` : ''}`;

    case 'NAPPY': {
      const parts = [];
      if (data.wet) parts.push('Wet');
      if (data.dirty) parts.push('Dirty');
      if (data.rash) parts.push('Rash noted');
      if (data.blowout) parts.push('💥 Blowout');
      return parts.join(', ') || 'Nappy change';
    }

    case 'SLEEP': {
      const dur = data.duration ? ` for ${formatMinutes(data.duration)}` : '';
      return `${data.sleepType === 'nap' ? 'Nap' : 'Sleep'}${dur}`;
    }

    case 'MEDICATION':
      return `${data.medicationName || 'Medication'}${data.dose ? ` - ${data.dose}` : ''}`;

    case 'WEIGHT':
      return `${data.weight}${data.unit || 'kg'}${data.height ? ` | ${data.height}${data.heightUnit || 'cm'}` : ''}`;

    case 'TEMPERATURE':
      return `${data.temperature}°${data.tempUnit || 'C'}${data.method ? ` (${data.method})` : ''}`;

    case 'TUMMY_TIME': {
      const fmtSecs = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
      const durationStr = data.duration ? fmtSecs(data.duration) : null;
      const headUpStr = data.headUpDuration ? ` (head up: ${fmtSecs(data.headUpDuration)})` : '';
      return `${durationStr ? durationStr : 'Tummy time'}${headUpStr}${data.mood ? ` - ${data.mood}` : ''}`;
    }

    case 'EC': {
      const peeLabel = data.ecPee && data.ecPee !== 'none' ? `Pee: ${data.ecPee}` : null;
      const poopLabel = data.ecPoop && data.ecPoop !== 'none' ? `Poop: ${data.ecPoop}` : null;
      const outcomes = [peeLabel, poopLabel].filter(Boolean).join(', ');
      return outcomes || 'EC — nothing this time';
    }

    case 'PUMP':
      const total = (data.leftAmount || 0) + (data.rightAmount || 0);
      return `Pumped ${total}ml`;

    case 'BOTTLE':
      return `${data.bottleType === 'formula' ? 'Formula' : 'Expressed milk'} ${data.amountConsumed || data.amountOffered || ''}ml`;

    case 'SPIT_UP':
      return data.severity ? `Spit up - ${data.severity}` : 'Spit up';

    case 'JOURNAL':
      return data.title || 'Journal entry';

    case 'SYMPTOM':
      return `${data.symptomType || 'Symptom'}${data.symptomSeverity ? ` - ${data.symptomSeverity}` : ''}`;

    case 'APPOINTMENT':
      return `${data.appointmentType || 'Appointment'}${data.providerName ? ` with ${data.providerName}` : ''}`;

    case 'BLOOD_PRESSURE':
      return `BP ${data.systolic || '?'}/${data.diastolic || '?'}${data.heartRate ? ` | HR ${data.heartRate}` : ''}`;

    case 'ULTRASOUND':
      return `${data.ultrasoundType || 'Ultrasound'}${data.gestationalAgeWeeks ? ` at ${data.gestationalAgeWeeks}w` : ''}`;

    case 'KICK_COUNT':
      return data.kickCount ? `${data.kickCount} kicks` : 'Kick count session';

    case 'CONTRACTION':
      return `Contraction${data.contractionDuration ? ` ${data.contractionDuration}s` : ''}${data.contractionIntensity ? ` (${data.contractionIntensity})` : ''}`;

    default:
      return type;
  }
}

export default function EventHistory() {
  const { babyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state — persisted in URL search params
  const selectedTypesParam = searchParams.get('types');
  const selectedTypes: EventType[] = selectedTypesParam
    ? (selectedTypesParam.split(',').filter(Boolean) as EventType[])
    : [];
  const [showFilters, setShowFilters] = useState(selectedTypes.length > 0);

  // Date range — default to last 7 days, expandable to all
  const showAllParam = searchParams.get('all') === '1';
  const startDate = showAllParam ? undefined : subDays(new Date(), 7).toISOString();

  // Decrypted events
  const [decryptedEvents, setDecryptedEvents] = useState<DecryptedEvent[]>([]);
  const [decryptionError, setDecryptionError] = useState<string | null>(null);

  const toggleTypeFilter = (type: EventType) => {
    const next = selectedTypes.includes(type)
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];
    setSearchParams(prev => {
      if (next.length > 0) {
        prev.set('types', next.join(','));
      } else {
        prev.delete('types');
      }
      return prev;
    });
  };

  const clearFilters = () => {
    setSearchParams(prev => {
      prev.delete('types');
      return prev;
    });
  };

  // Fetch events
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['events', babyId, selectedTypes, showAllParam],
    queryFn: async () => {
      if (!babyId) return { success: true, data: { events: [] } };
      const params: Record<string, string> = { limit: '500' };
      if (selectedTypes.length > 0) {
        params.types = selectedTypes.join(',');
      }
      if (startDate) {
        params.startDate = startDate;
      }
      const response = await eventApi.list(babyId, params);
      return response;
    },
    enabled: !!babyId,
  });

  // Fetch baby profileType for conditional filter chips
  const { data: babyProfileType } = useQuery({
    queryKey: ['babyProfileType', babyId],
    queryFn: async () => {
      if (!babyId) return null;
      const aesKey = await getBabyKey(babyId);
      if (!aesKey) return null;
      const response = await babyApi.getById(babyId);
      const baby = response.data || response;
      if (!baby?.encryptedData) return null;
      try {
        const decoded = await decryptBabyProfile(baby.encryptedData, aesKey);
        return decoded?.profileType as ProfileType | undefined;
      } catch {
        return null;
      }
    },
    enabled: !!babyId,
  });

  // Determine which filter chips to show
  const filterEventTypes = babyProfileType === 'PREGNANCY' ? PREGNANCY_EVENT_TYPES : BABY_EVENT_TYPES;

  // Decrypt events when data changes
  useEffect(() => {
    async function decryptEvents() {
      if (!eventsData?.data || !babyId) return;

      try {
        const aesKey = await getBabyKey(babyId);
        if (!aesKey) {
          setDecryptionError('Could not find encryption key');
          return;
        }

        const decrypted: DecryptedEvent[] = [];

        for (const event of (eventsData.data?.events ?? []) as Event[]) {
          try {
            const data = await decryptEventData(event.encryptedData, aesKey);
            decrypted.push({
              id: event.id,
              type: event.type,
              timestamp: new Date(event.timestamp),
              data,
              notes: event.encryptedNotes ? await decryptData(event.encryptedNotes, aesKey) : null,
              photos: event.photos,
              createdById: event.createdById,
              createdByName: event.createdByName,
              createdAt: new Date(event.createdAt),
              updatedAt: new Date(event.updatedAt),
            });
          } catch (err) {
            console.error(`Failed to decrypt event ${event.id}:`, err);
          }
        }

        setDecryptedEvents(decrypted);
        setDecryptionError(null);
      } catch (err) {
        console.error('Failed to decrypt events:', err);
        setDecryptionError('Failed to decrypt events');
      }
    }

    decryptEvents();
  }, [eventsData, babyId]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2 text-sm"
          >
            &larr; Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Event History
          </h1>
        </div>

        <Link
          to={`/app/baby/${babyId}/event/new`}
          className="btn-primary"
        >
          + Log Event
        </Link>
      </div>

      {/* Date range toggle */}
      <div className="mb-2 flex items-center gap-3">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {showAllParam ? 'Showing all history' : 'Showing last 7 days'}
        </span>
        <button
          onClick={() => setSearchParams(prev => {
            if (showAllParam) { prev.delete('all'); } else { prev.set('all', '1'); }
            return prev;
          })}
          className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          {showAllParam ? 'Show last 7 days' : 'Show all history'}
        </button>
      </div>

      {/* Filter toggle */}
      <div className="mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          {showFilters ? 'Hide filters' : 'Show filters'}
          {selectedTypes.length > 0 && ` (${selectedTypes.length} active)`}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="card p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900 dark:text-white">Filter by type</h3>
            {selectedTypes.length > 0 && (
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {filterEventTypes.map(type => {
              const info = EVENT_TYPE_INFO[type]!;
              const isSelected = selectedTypes.includes(type);
              return (
                <button
                  key={type}
                  onClick={() => toggleTypeFilter(type)}
                  className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-colors border ${
                    isSelected
                      ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-800 dark:text-primary-200'
                      : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <span>{info.icon}</span>
                  <span>{info.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Error message */}
      {decryptionError && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
          {decryptionError}
        </div>
      )}

      {/* Events list */}
      {decryptedEvents.length === 0 ? (
        <div className="text-center py-12">
          <span className="text-4xl">📋</span>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            {selectedTypes.length > 0
              ? 'No events match your filters'
              : 'No events logged yet'}
          </p>
          <Link
            to={`/app/baby/${babyId}/event/new`}
            className="btn-primary mt-4"
          >
            Log First Event
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {decryptedEvents.map(event => {
            const info = EVENT_TYPE_INFO[event.type] || { icon: '📋', label: event.type, color: 'bg-gray-100' };

            return (
              <Link
                key={event.id}
                to={`/app/baby/${babyId}/event/${event.id}`}
                className={`block card p-4 border hover:shadow-md transition-shadow ${info.color}`}
              >
                <div className="flex items-start gap-4">
                  <span className="text-3xl">{info.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {info.label}
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDistanceToNow(event.timestamp, { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      {getEventSummary(event.type, event.data)}
                    </p>
                    {event.notes && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic truncate">
                        {event.notes}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {formatEventTime(event.timestamp)} • by {event.createdByName}
                    </p>
                    {event.photos.length > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        📷 {event.photos.length} photo{event.photos.length > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Quick action button — positioned above bottom nav on mobile */}
      <div className="fixed bottom-24 right-6 sm:bottom-6">
        <Link
          to={`/app/baby/${babyId}/event/new`}
          className="btn-primary shadow-lg rounded-full w-14 h-14 flex items-center justify-center text-2xl"
        >
          +
        </Link>
      </div>
    </div>
  );
}
